import { PanelModel } from '../../../models/panel.model'
import type { GMXPanel } from '../panel/panel'

/**
 * Panel Singleton - Picks up click events on the page and opens associated page/body panels
 */
class GMXPanelSingleton {
	intersectionObserver: IntersectionObserver
	mutationObserver: MutationObserver

	constructor() {
		if (!window.gmxPanelSingleton) {
			window.gmxPanelSingleton = this

			this.setupPanelShowListener()
			this._addPanelObserver()
		} else {
			window.console.log('Tried to instantiate panel singleton...already instantiated')
		}
	}

	/**
	 * Method to setup a click event listener on the body, looking for elements that were clicked that are
	 * to open an panel on the page, ie, has attribute [data-panel-reveal-id].  If a panel id is found,
	 * then we'll look to see if its already loaded on the page, else, fetch and insert at the bottom of the body
	 */
	public setupPanelShowListener(root: ShadowRoot | HTMLElement = document.body): void {
		root.addEventListener('click', e => {
			let target = e.target as HTMLElement

			let targetPanelId = target.getAttribute('data-panel-reveal-id')

			if (!targetPanelId) {
				// Panel id not found, look up through ancestors to see if a child element captured the click event
				target = target.closest('[data-panel-reveal-id]') as HTMLElement

				if (target) {
					targetPanelId = target.getAttribute('data-panel-reveal-id')
				}
			}

			// If we didn't find a target panel id, the click event wasn't for an panel so pass through without
			// doing anything
			if (targetPanelId) {
				const targetPanelType = target.getAttribute('data-panel-type')
				const targetActiveTabSet = target.getAttribute('data-active-tab-set')
				const targetPanelEl = root.querySelector(`[id="${targetPanelId}"]`)
				const triggerPanelData = this._parsePanelDataAttribute(target)

				if (targetPanelEl) {
					if (targetPanelType === 'gallery-panel' && targetActiveTabSet) {
						targetPanelEl.setAttribute('data-default-gallery-tab', targetActiveTabSet || '')
					}

					this._assignPanelData(targetPanelEl as HTMLElement, triggerPanelData)
					this._revealPanel(targetPanelEl as HTMLElement)
				} else if (target.hasAttribute('data-panel-reveal-id')) {
					window.console.warn(`Error:  Panel with id ${targetPanelId} not found..try to fetch`)

					this._insertNewPanelElement(
						targetPanelId,
						targetPanelType,
						true,
						root,
						targetActiveTabSet,
						triggerPanelData
					)
				}
			}
		})
	}

	/**
	 * Setup an IntersectionObserver to observe for when panel trigger elements (those that have attributte
	 * [data-panel-reveal-id]) come close to the viewport and if observed, look to see if its already been loaded
	 * and if not, load and insert at the bottom of the body.
	 *
	 * @private
	 */
	private _addPanelObserver(): void {
		this.intersectionObserver = new IntersectionObserver(
			entries => {
				entries.forEach(entry => {
					if (entry.isIntersecting) {
						const targetEl = entry.target
						const targetPanelId = targetEl.getAttribute('data-panel-reveal-id'),
							targetPanelType = targetEl.getAttribute('data-panel-type'),
							targetActiveTabSet = targetEl.getAttribute('data-active-tab-set'),
							targetPanelData = this._parsePanelDataAttribute(targetEl as HTMLElement)

						if (targetPanelId) {
							// If the target panel id exists and the element already exists, pass through as it is
							// already in the document.  Else if it isn't found, load and insert at the bottom of the body
							if (document.getElementById(targetPanelId) === null) {
								// Only insert a new element if you didn't find the one with the id you are looking for
								this._insertNewPanelElement(
									targetPanelId,
									targetPanelType,
									false,
									document.body,
									targetActiveTabSet,
									targetPanelData
								)
							}
						}

						this.intersectionObserver.unobserve(targetEl)
					}
				})
			},
			{
				root: null,
				rootMargin: '250px',
				threshold: 0,
			}
		)

		const panelTriggerEls = document.body.querySelectorAll('[data-panel-reveal-id]')

		panelTriggerEls.forEach(el => {
			this.intersectionObserver.observe(el)
		})

		this.mutationObserver = new MutationObserver(this._onDOMMutation.bind(this))

		this.mutationObserver.observe(document.getElementsByTagName('body')[0], {
			childList: true,
			subtree: true,
		})
	}

	/**
	 * Callback for the MutationObserver, looking for any added nodes that have the attribute [data-panel-reveal-id] or contain children with that attribute, and if so, add those elements to be observed by the IntersectionObserver
	 * @param mutations
	 * @private
	 */
	private _onDOMMutation(mutations: MutationRecord[]): void {
		if (Array.isArray(mutations)) {
			mutations.forEach(mut => {
				const target = mut.target
				const addedNode = mut.addedNodes && mut.addedNodes[0]

				if (target && target.nodeType === 1) {
					const panelTriggerEls: HTMLElement[] = []
					mut.addedNodes.forEach((node: Node) => {
						if (node && node.nodeType === 1) {
							const el = node as HTMLElement
							if (el.hasAttribute('data-panel-reveal-id')) {
								panelTriggerEls.push(el)
							} else {
								const childPanelTriggerEls = Array.from(
									el.querySelectorAll('[data-panel-reveal-id]')
								) as HTMLElement[]

								if (childPanelTriggerEls.length > 0) {
									panelTriggerEls.push(...childPanelTriggerEls)
								}
							}
						}
					})

					panelTriggerEls.forEach((te: HTMLElement) => {
						this.intersectionObserver.observe(te)
					})
				}

				if (target && target.nodeType === 1) {
					if (addedNode && addedNode.nodeType === 1) {
						const panelTriggerEls = (addedNode as HTMLElement).querySelectorAll('[data-panel-reveal-id]')

						panelTriggerEls.forEach((te: HTMLElement) => {
							this.intersectionObserver.observe(te)
						})
					}
				}
			})
		}
	}

	/**
	 * Open an existing panel. If it is already open, bring it to the front of the stack.
	 * @param targetPanelEl
	 * @private
	 */
	private _revealPanel(targetPanelEl: HTMLElement): void {
		if (targetPanelEl.hasAttribute('data-show') && targetPanelEl.hasAttribute('data-panel-loaded')) {
			;(targetPanelEl as GMXPanel).showPanel?.()
		} else if (!targetPanelEl.hasAttribute('data-show')) {
			targetPanelEl.setAttribute('data-show', '')
		}
	}

	/**
	 * Insert a new panel element at the bottom of the document/body
	 * @param panelPartialUrl {string} - url of the panel content
	 * @param targetPanelType {string} - type of panel, to determine the panel treatment/UX
	 * @param showImmediate {boolean} - if set to true, tells the panel that once you've loaded the content, immediately show/open the panel
	 * @private
	 */
	private _insertNewPanelElement(
		panelPartialUrl: string,
		targetPanelType: string,
		showImmediate: boolean,
		root: ShadowRoot | HTMLElement = document.body,
		targetActiveTabSet: string = null,
		panelData: Record<string, string> = null
	): void {
		const newPanelEl = document.createElement('gmx-panel')
		newPanelEl.setAttribute('id', panelPartialUrl)
		newPanelEl.setAttribute('data-panel-type', targetPanelType)

		if (panelData) {
			newPanelEl.setAttribute('data-panel-data', JSON.stringify(panelData))
		}

		if (showImmediate) {
			if (targetPanelType === 'gallery-panel' && targetActiveTabSet) {
				newPanelEl.setAttribute('data-default-gallery-tab', targetActiveTabSet || '')
			}

			newPanelEl.setAttribute('data-show', '')
		}

		root.appendChild(newPanelEl)
	}

	/**
	 * For the Injector component, want to preload all panels within the ShadowRoot of each calling injector.
	 * @param root
	 * @param panels
	 */
	public prefetchPanelsForInjector(root: ShadowRoot, panels: PanelModel[]): void {
		panels.forEach(panel => {
			const newPanelEl = document.createElement('gmx-panel')
			newPanelEl.setAttribute('id', panel.id)
			newPanelEl.setAttribute('data-panel-type', panel.type)

			root.appendChild(newPanelEl)
		})
	}

	/**
	 * Method to trigger/show an panel with a given id.  If the panel with the given id is already on the page, will simply pop it open, else, will create a new panel element with the given id and type and insert at the bottom of the body.
	 * @param panelPartialUrl
	 * @param targetPanelType
	 * @param panelData
	 */
	public triggerPanel(
		panelPartialUrl: string,
		targetPanelType: string,
		panelData?: Record<string, string>,
		targetActiveTabSet?: string
	): void {
		const targetPanelEl = document.getElementById(panelPartialUrl)

		if (targetPanelEl) {
			if (targetPanelType === 'gallery-panel' && targetActiveTabSet) {
				targetPanelEl.setAttribute('data-default-gallery-tab', targetActiveTabSet || '')
			}

			this._assignPanelData(targetPanelEl, panelData || null)

			this._revealPanel(targetPanelEl)
		} else {
			this._insertNewPanelElement(
				panelPartialUrl,
				targetPanelType,
				true,
				document.body,
				targetActiveTabSet,
				panelData
			)
		}
	}

	/**
	 * Parse data-panel-data from a trigger element. Returns null when the attribute is missing or invalid.
	 */
	private _parsePanelDataAttribute(el: HTMLElement): Record<string, string> | null {
		const rawPanelData = el.getAttribute('data-panel-data')
		if (!rawPanelData) {
			return null
		}

		try {
			return JSON.parse(rawPanelData)
		} catch {
			return null
		}
	}

	/**
	 * Copy the current trigger's panel data onto the panel when data is present.
	 */
	private _assignPanelData(panelEl: HTMLElement, panelData: Record<string, string> | null): void {
		if (panelData) {
			panelEl.setAttribute('data-panel-data', JSON.stringify(panelData))
		}
	}
}

const panelSingleton = Object.freeze(new GMXPanelSingleton())
export default panelSingleton
